import React, { useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import { sans, mono, green, greenLight, useTheme } from '../../tokens';

const inputStyle = (t) => ({
  fontFamily: sans,
  fontSize: 12,
  padding: '8px 12px',
  border: '1px solid ' + t.border,
  borderRadius: 6,
  background: t.inputBg,
  color: t.textPrimary,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
});

const labelStyle = (t) => ({
  fontSize: 11,
  fontWeight: 600,
  color: t.textSecondary,
  fontFamily: sans,
  marginBottom: 6,
  display: 'block',
});

const sectionTitleStyle = (t) => ({
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: t.textMuted,
  fontFamily: sans,
  marginBottom: 16,
  paddingBottom: 8,
  borderBottom: '1px solid ' + t.border,
});

const toggleStyle = (enabled, t) => ({
  width: 36,
  height: 20,
  borderRadius: 10,
  background: enabled ? green : t.border,
  position: 'relative',
  cursor: 'pointer',
  transition: 'background 0.15s',
  flexShrink: 0,
});

const toggleKnob = (enabled) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: '#fff',
  position: 'absolute',
  top: 2,
  left: enabled ? 18 : 2,
  transition: 'left 0.15s',
  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
});

const INITIAL = {
  minLength: 12,
  requireUppercase: true,
  requireNumber: true,
  requireSpecial: true,
  passwordExpiration: 'never',
  twoFaEnforcement: 'optional',
  sessionTimeout: '30',
};

export default function SecuritySettings({ hasMfaEnrolled, showMfaSetup, setShowMfaSetup }) {
  const t = useTheme();
  const savedSecurity = JSON.parse(localStorage.getItem('finkloz_security_settings') || 'null');
  const [form, setForm] = useState(savedSecurity || INITIAL);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loginSearch, setLoginSearch] = useState('');
  const [historyVersion, setHistoryVersion] = useState(0);

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSave = () => {
    localStorage.setItem('finkloz_security_settings', JSON.stringify(form));
    setSaved(true);
    setDirty(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const loginHistory = JSON.parse(localStorage.getItem('finkloz_login_history') || '[]');

  const filteredLogins = loginSearch
    ? loginHistory.filter(l =>
        (l.email || '').toLowerCase().includes(loginSearch.toLowerCase()) ||
        (l.device || '').toLowerCase().includes(loginSearch.toLowerCase()) ||
        l.status.includes(loginSearch.toLowerCase())
      )
    : loginHistory;

  const handleClearHistory = () => {
    if (!window.confirm('Clear all login history?')) return;
    localStorage.removeItem('finkloz_login_history');
    setHistoryVersion(v => v + 1);
  };

  const formatTimestamp = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-CA', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      });
    } catch {
      return iso;
    }
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans, margin: '0 0 4px' }}>
        Security
      </h2>
      <p style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, margin: '0 0 24px' }}>
        Password policies, two-factor authentication, and session management.
      </p>

      {/* Password Policy */}
      <div style={sectionTitleStyle(t)}>Password Policy</div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>Minimum Password Length: {form.minLength} characters</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 11, color: t.textMuted, fontFamily: mono }}>8</span>
          <input
            type="range"
            min={8}
            max={20}
            value={form.minLength}
            onChange={e => update('minLength', parseInt(e.target.value))}
            style={{ flex: 1, maxWidth: 300, accentColor: green }}
          />
          <span style={{ fontSize: 11, color: t.textMuted, fontFamily: mono }}>20</span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        {[
          { key: 'requireUppercase', label: 'Require uppercase letter' },
          { key: 'requireNumber', label: 'Require number' },
          { key: 'requireSpecial', label: 'Require special character' },
        ].map(item => (
          <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              onClick={() => update(item.key, !form[item.key])}
              style={toggleStyle(form[item.key], t)}
            >
              <div style={toggleKnob(form[item.key])} />
            </div>
            <span style={{ fontSize: 12, color: t.textPrimary, fontFamily: sans }}>{item.label}</span>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>Password Expiration</label>
        <select
          value={form.passwordExpiration}
          onChange={e => update('passwordExpiration', e.target.value)}
          style={{ ...inputStyle(t), maxWidth: 300 }}
        >
          <option value="never">Never</option>
          <option value="30">Every 30 days</option>
          <option value="60">Every 60 days</option>
          <option value="90">Every 90 days</option>
        </select>
      </div>

      {/* Two-Factor Authentication */}
      <div style={{ ...sectionTitleStyle(t), marginTop: 32 }}>Two-Factor Authentication</div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>2FA Enforcement</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { value: 'required_all', label: 'Required for all users' },
            { value: 'required_role', label: 'Required by role (Admin, Controller)' },
            { value: 'optional', label: 'Optional' },
          ].map(opt => (
            <label key={opt.value} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 12, fontFamily: sans, color: t.textPrimary, cursor: 'pointer',
            }}>
              <input
                type="radio"
                name="twoFaEnforcement"
                value={opt.value}
                checked={form.twoFaEnforcement === opt.value}
                onChange={() => update('twoFaEnforcement', opt.value)}
                style={{ accentColor: green }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      {/* MFA Setup */}
      <div style={{ marginBottom: 20, padding: '12px 16px', background: t.bgSurface, borderRadius: 8, border: `1px solid ${t.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, fontFamily: sans, color: t.textPrimary }}>
              Your MFA Status: {hasMfaEnrolled ? 'Enrolled' : 'Not enrolled'}
            </div>
            <div style={{ fontSize: 10, fontFamily: sans, color: t.textMuted, marginTop: 2 }}>
              {hasMfaEnrolled ? 'Two-factor authentication is active on your account.' : 'Enable two-factor authentication for your account.'}
            </div>
          </div>
          {!hasMfaEnrolled && setShowMfaSetup && (
            <button onClick={() => setShowMfaSetup(true)} style={{
              border: `1px solid ${green}`, background: greenLight,
              borderRadius: 6, padding: '6px 14px', cursor: 'pointer',
              fontSize: 11, fontWeight: 600, fontFamily: sans, color: green,
            }}>Enable MFA</button>
          )}
          {hasMfaEnrolled && (
            <span style={{
              fontSize: 10, fontWeight: 700, fontFamily: sans,
              padding: '4px 10px', borderRadius: 12,
              background: greenLight, color: green,
            }}>Active</span>
          )}
        </div>
      </div>

      {/* Session Management */}
      <div style={{ ...sectionTitleStyle(t), marginTop: 32 }}>Session Management</div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>Session Timeout</label>
        <select
          value={form.sessionTimeout}
          onChange={e => update('sessionTimeout', e.target.value)}
          style={{ ...inputStyle(t), maxWidth: 300 }}
        >
          <option value="15">15 minutes</option>
          <option value="30">30 minutes</option>
          <option value="60">60 minutes</option>
          <option value="120">120 minutes</option>
        </select>
      </div>

      {/* Login History */}
      <div style={{ ...sectionTitleStyle(t), marginTop: 32 }}>Login History</div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: t.textMuted }} />
          <input
            type="text"
            placeholder="Search logins..."
            value={loginSearch}
            onChange={e => setLoginSearch(e.target.value)}
            style={{ ...inputStyle(t), paddingLeft: 32 }}
          />
        </div>
        {loginHistory.length > 0 && (
          <button
            onClick={handleClearHistory}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px',
              border: '1px solid ' + t.border,
              borderRadius: 6,
              background: 'transparent',
              color: '#C62828',
              fontSize: 11,
              fontWeight: 600,
              fontFamily: sans,
              cursor: 'pointer',
            }}
          >
            <Trash2 size={12} />
            Clear History
          </button>
        )}
      </div>

      <div style={{
        border: '1px solid ' + t.border,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 32,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sans, fontSize: 12 }}>
          <thead>
            <tr style={{ background: t.bgSurface }}>
              {['User', 'Timestamp', 'Device', 'Status'].map(h => (
                <th key={h} style={{
                  padding: '10px 14px',
                  textAlign: 'left',
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: t.textMuted,
                  borderBottom: '1px solid ' + t.border,
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredLogins.length === 0 ? (
              <tr>
                <td colSpan={4} style={{
                  padding: '24px 14px',
                  textAlign: 'center',
                  color: t.textMuted,
                  fontSize: 12,
                  fontFamily: sans,
                }}>
                  No login history recorded yet.
                </td>
              </tr>
            ) : (
              filteredLogins.map((l, i) => (
                <tr key={i} style={{ borderBottom: '1px solid ' + t.borderLight }}>
                  <td style={{ padding: '10px 14px', color: t.textPrimary, fontWeight: 500 }}>
                    {l.email || '--'}
                  </td>
                  <td style={{ padding: '10px 14px', color: t.textSecondary, fontFamily: mono, fontSize: 11 }}>
                    {formatTimestamp(l.timestamp)}
                  </td>
                  <td style={{ padding: '10px 14px', color: t.textSecondary, fontSize: 11 }}>
                    {l.device || '--'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{
                      padding: '3px 8px',
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      background: l.status === 'success' ? greenLight : '#FCEBEB',
                      color: l.status === 'success' ? green : '#C0392B',
                    }}>
                      {l.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!dirty}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: 6,
            background: dirty ? green : t.border,
            color: dirty ? '#fff' : t.textMuted,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: sans,
            cursor: dirty ? 'pointer' : 'default',
            transition: 'all 0.15s',
          }}
        >
          Save
        </button>
        {saved && (
          <span style={{ fontSize: 12, color: green, fontFamily: sans, fontWeight: 500 }}>
            Settings saved
          </span>
        )}
      </div>
    </div>
  );
}
